import React, {Suspense, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {useHistory, useParams} from 'react-router';
import {useSelector} from 'react-redux';
import {selectCurrentUser, selectHydrationFinished, selectSsrRendered} from 'app/hooks/reduxCreateSelectorHooks';
import {checkPlayAd, setAdCredits} from 'app/helpers/adStepper';
import {useVideoStartTracking} from 'app/components/tv/trackVideoSeeking';
import {LOADING_MESSAGE, LoadingIndicator} from '@fupa/fupa-uikit';
import {updateCurrentVideo} from 'app/routes/tv/TvPlaylistPageActions';
import {updateLastViewed} from 'app/components/tv/helpers/lastViewedVideos';
import {trackVideoProgress} from 'app/components/tv/helpers/trackVideoProgress';
import {LoadingOverlay} from 'app/components/tv/LoadingOverlay';
import {captureSentryException} from 'app/services/sentryLogging';
import VideoCreator from 'app/components/tv/models/VideoCreator';
const VideoJS = React.lazy(() => import('app/components/tv/VideoJS'));

// scrollable wrapper necessary to hide address-bar in mobile Browsers
// for chrome ios it's not enough to set 101vh to hide address-bar
// -> therefore it's set to 200vh to hopefully support all browsers
const Wrapper = styled.div`
  position: relative;
  width: 100vw;
  height: 200vh;
  background-color: black;
  padding-top: env(safe-area-inset-top);
`;

// Player-Container is fixed with height 100%
// to always have the height of the visible screen on mobile browsers,
// no matter if address-bar is shown or not
const VideoPlayerContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const getKeywords = video => {
  return {
    competition: video?.match?.competition?.slug,
    homeTeam: video?.match?.homeTeam?.clubSlug,
    awayTeam: video?.match?.awayTeam?.clubSlug,
    region:
      video?.match?.competition?.slug !== 'testspiele'
        ? video?.match?.competition?.district?.region?.slug
        : video?.user?.district?.region?.slug,
    source: video?.category,
  };
};

const VideoPlayer = ({video, playlist, isFetching, dispatch, ...props}) => {
  const currentVideoRef = useRef(null);
  const playerRef = useRef(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [isNetworkError, setIsNetworkError] = useState(false);

  const history = useHistory();
  const {matchPlaylistSlug} = useParams();

  const keywords = getKeywords(video);
  const url = `https://www.fupa.net${history.location.pathname}`;
  const {ppid} = useSelector(selectCurrentUser);
  const ssrRendered = useSelector(selectSsrRendered);
  const hydrationFinished = useSelector(selectHydrationFinished);

  useEffect(() => {
    const player = playerRef.current;
    return () => {
      if (player) {
        // update credits when site is left before video ended
        trackVideoProgress(player.played(), currentVideoRef.current.startSec);
      }
    };
  }, [playerReady]);

  useEffect(() => {
    if (!Object.keys(video).length) {
      return;
    }
    currentVideoRef.current = video;
    if (VideoCreator.createVideo(video).isSeekable()) {
      setAdCredits(0);
    }
    if (!ssrRendered && video) {
      const currentVideoIndex = playlist.indexOf(playlist.find(item => item.slug === video.slug));
      updateLastViewed(video.matchPlaylistId, currentVideoIndex);
    }
  }, [video]);

  useVideoStartTracking(playerRef.current, video, playerReady, playlist);

  if (!video || !Object.keys(video).length) {
    if (isFetching) {
      return <LoadingIndicator message={LOADING_MESSAGE.loadingData} bgColor={false} />;
    } else {
      return null;
    }
  }

  const setCurrentVideo = newCurrentVideo => {
    dispatch(updateCurrentVideo(newCurrentVideo));
    history.replace(`/tv/match/${matchPlaylistSlug}/${newCurrentVideo.slug}`);
  };

  const handlePlayerReady = player => {
    playerRef.current = player;
    setPlayerReady(true);

    player.on('ended', () => {
      trackVideoProgress(player.played(), currentVideoRef.current.startSec);
    });

    player.on('error', () => {
      if (player.error().code === player.error().MEDIA_ERR_NETWORK) {
        setIsNetworkError(true);
      }
      logVjsError(player.error(), player.currentSrc());
      player.play();
    });

    player.on('stalled', () => {
      captureSentryException(new Error(`Video stalled in request`), {
        extra: {
          source: player.currentSrc(),
        },
      });
    });

    player.on('adserror', error => {
      const playContent = logAdError(error, player.currentSrc());
      if (playContent) {
        player.play();
      }
    });
  };

  const Player = hydrationFinished ? (
    <Suspense fallback={<LoadingOverlay show={true} />}>
      <VideoJS
        onReady={handlePlayerReady}
        keywords={keywords}
        url={url}
        ppid={ppid}
        video={video}
        playlist={playlist}
        setCurrentVideo={setCurrentVideo}
        playerReady={playerReady}
        isNetworkError={isNetworkError}
        setIsNetworkError={setIsNetworkError}
        {...props}
      />
    </Suspense>
  ) : (
    <LoadingOverlay show={true} />
  );

  return (
    <Wrapper>
      <VideoPlayerContainer>{Player}</VideoPlayerContainer>
    </Wrapper>
  );
};

export {VideoPlayer};

const logVjsError = (error, src) => {
  if (error.code === error.MEDIA_ERR_SRC_NOT_SUPPORTED) {
    captureSentryException(new Error('MediaError: Source is not supported'), {
      extra: {type: 'vjs error', errorInfo: error, src, playAd: checkPlayAd()},
    });
  } else {
    captureSentryException(new Error(`VideoJS error-handler: ${error.message}`), {
      extra: {
        errorInfo: {type: 'vjs error', code: error.code, message: error.message, status: error.status},
        src,
      },
    });
  }
};

const AutoplayPreventedCode = 1205;
const NoVastResponseCode = 303;

const logAdError = (error, src) => {
  const adErrorData = error.data?.AdError?.data;
  if (adErrorData) {
    const innerError = adErrorData.innerError;
    if (
      innerError?.errorCode === AutoplayPreventedCode ||
      innerError?.code === AutoplayPreventedCode ||
      adErrorData.code === AutoplayPreventedCode ||
      adErrorData.errorCode === AutoplayPreventedCode
    ) {
      return false;
    }
    if (adErrorData.code === NoVastResponseCode || adErrorData.errorCode === NoVastResponseCode) {
      // expected error as there is no 100% fillrate
      return true;
    }
    captureSentryException(new Error('Ads Error with additional info'), {
      extra: {
        errorInfo: adErrorData,
        src,
      },
    });
    return true;
  }
  captureSentryException(new Error('Ads Error without additional info'), {
    extra: {
      errorInfo: error,
      src,
    },
  });
  return true;
};
